import { defineStore } from 'pinia'
import { EaseChatSDK, EaseChatClient } from '@/IM/initwebsdk'
import { message } from 'ant-design-vue'
import { useImConversationStore, useImInfoStore } from '@/store'
import dayjs from 'dayjs'
import tool from '@/utils/tool'
import router from '@/router'
import imuserApi from '@/api/onlineChat/imuserApi'

const isNotLoginError = (e) => {
    if (!e) return false
    if (String(e.type) === '39' || String(e.code) === '39' || String(e.code) === 'NOT_LOGIN') return true
    return /not\s*login/i.test(String(e.message || '')) || /未登录/i.test(String(e.message || ''))
}

export const useImMessageStore = defineStore({
    id: 'imMessage',
    state: () => ({
        currentChat: {
            lastMessage: {},
        },
        messageList: [],
    }),
    actions: {
        getMessageList(cursor = '-1') {
            return new Promise((resolve) => {
                const options = {
                    cursor,
                    targetId: this.currentChat.conversationId,
                    pageSize: 10,
                    chatType: 'singleChat',
                    searchDirection: 'up',
                    searchOptions: {
                        startTime: dayjs().subtract(30, 'day').valueOf(),
                        endTime: dayjs().endOf('day').valueOf(),
                    },
                }
                EaseChatClient.getHistoryMessages(options).then((res) => {
                    this.messageList = [...this.messageList, ...res.messages]
                    resolve(res)
                }).catch((e) => {
                    message.error(`获取历史消息失败：[${e.type}]${e.message}`)
                    resolve()
                })
            })
        },
        updateCurrentChat(data) {
            this.currentChat = data
        },
        updateMessageList(msgObj) {
            this.messageList.unshift(msgObj)
        },
        clearMessageList() {
            this.messageList = []
        },
        // 返回 true 表示发送成功；失败返回 false（让组件决定是否继续跳转）
        sendPersonMessage(userId, { retryOnNotLogin = true } = {}) {
            return new Promise(async (resolve) => {
                try {
                    await useImInfoStore().getImInfo()
                    const res = await imuserApi.imByCompany({ userId })
                    const ok = await this.sendTxtMessage('你好，看了你的简历，方便聊聊吗？', res.id, { retryOnNotLogin })
                    if (!ok) return resolve(false)
                    tool.session.set('conversationId', res.id)
                    tool.data.set('conversationId', res.id)
                    const url = router.resolve({ path: '/onlineChat' }).href
                    window.open(url, '_blank')
                    resolve(true)
                } catch (e) {
                    if (retryOnNotLogin && isNotLoginError(e)) {
                        try {
                            await useImInfoStore().getImInfo({ forceRefresh: true })
                            const ok = await this.sendPersonMessage(userId, { retryOnNotLogin: false })
                            return resolve(ok)
                        } catch (_) {}
                    }
                    if (!isNotLoginError(e)) {
                        message.error(`发起聊天失败：[${e.type || ''}]${e.message || ''}`)
                    }
                    resolve(false)
                }
            })
        },
        sendTxtMessage(msg, to, { retryOnNotLogin = true } = {}) {
            return new Promise(async (resolve) => {
                try {
                    await useImInfoStore().getImInfo()
                    const msgBody = EaseChatSDK.message.create({
                        type: 'txt',
                        chatType: 'singleChat',
                        msg,
                        to,
                    })
                    const sendRes = await EaseChatClient.send(msgBody)
                    const { serverMsgId } = sendRes || {}
                    msgBody.id = serverMsgId
                    msgBody.from = EaseChatClient.user
                    this.updateMessageList(msgBody)
                    setTimeout(() => useImConversationStore().getConversationList(), 500)
                    resolve(true)
                } catch (e) {
                    if (retryOnNotLogin && isNotLoginError(e)) {
                        try {
                            await useImInfoStore().getImInfo({ forceRefresh: true })
                            const ok = await this.sendTxtMessage(msg, to, { retryOnNotLogin: false })
                            return resolve(ok)
                        } catch (_) {}
                    }
                    message.error(`发送失败请重试：[${e.type}]${e.message}`)
                    useImInfoStore().getImInfo({ forceRefresh: true })
                    resolve(false)
                }
            })
        },
        sendImgMessage(to, file, width, height, { retryOnNotLogin = true } = {}) {
            return new Promise(async (resolve) => {
                try {
                    await useImInfoStore().getImInfo()
                    const option = {
                        file,
                        to,
                        width,
                        height,
                        type: 'img',
                        chatType: 'singleChat',
                        ext: {},
                        onFileUploadError: function () {},
                        onFileUploadProgress: function () {},
                        onFileUploadComplete: function () {},
                    }
                    const msgBody = EaseChatSDK.message.create(option)
                    const sendRes = await EaseChatClient.send(msgBody)
                    const { serverMsgId } = sendRes || {}
                    msgBody.id = serverMsgId
                    msgBody.from = EaseChatClient.user
                    this.updateMessageList(msgBody)
                    setTimeout(() => useImConversationStore().getConversationList(), 500)
                    resolve(true)
                } catch (e) {
                    if (retryOnNotLogin && isNotLoginError(e)) {
                        try {
                            await useImInfoStore().getImInfo({ forceRefresh: true })
                            const ok = await this.sendImgMessage(to, file, width, height, { retryOnNotLogin: false })
                            return resolve(ok)
                        } catch (_) {}
                    }
                    message.error(`发送失败请重试：[${e.type}]${e.message}`)
                    useImInfoStore().getImInfo({ forceRefresh: true })
                    resolve(false)
                }
            })
        },
        sendJobMessage(to, customExts = {}, ext = {}, { retryOnNotLogin = true } = {}) {
            return new Promise(async (resolve) => {
                try {
                    await useImInfoStore().getImInfo()
                    const option = {
                        type: 'custom',
                        chatType: 'singleChat',
                        customEvent: '',
                        customExts,
                        ext,
                        to,
                    }
                    const msgBody = EaseChatSDK.message.create(option)
                    const sendRes = await EaseChatClient.send(msgBody)
                    const { serverMsgId } = sendRes || {}
                    msgBody.id = serverMsgId
                    msgBody.from = EaseChatClient.user
                    this.updateMessageList(msgBody)
                    setTimeout(() => useImConversationStore().getConversationList(), 500)
                    resolve(true)
                } catch (e) {
                    if (retryOnNotLogin && isNotLoginError(e)) {
                        try {
                            await useImInfoStore().getImInfo({ forceRefresh: true })
                            const ok = await this.sendJobMessage(to, customExts, ext, { retryOnNotLogin: false })
                            return resolve(ok)
                        } catch (_) {}
                    }
                    message.error(`发送失败请重试：[${e.type}]${e.message}`)
                    useImInfoStore().getImInfo({ forceRefresh: true })
                    resolve(false)
                }
            })
        },
        sendCustomMessage(to, customExts = {}, ext = {}, { retryOnNotLogin = true } = {}) {
            return new Promise(async (resolve) => {
                try {
                    await useImInfoStore().getImInfo()
                    const option = {
                        type: 'custom',
                        chatType: 'singleChat',
                        customEvent: '',
                        customExts,
                        ext,
                        to,
                    }
                    const msgBody = EaseChatSDK.message.create(option)
                    const sendRes = await EaseChatClient.send(msgBody)
                    const { serverMsgId } = sendRes || {}
                    msgBody.id = serverMsgId
                    msgBody.from = EaseChatClient.user
                    this.updateMessageList(msgBody)
                    setTimeout(() => useImConversationStore().getConversationList(), 500)
                    resolve(true)
                } catch (e) {
                    if (retryOnNotLogin && isNotLoginError(e)) {
                        try {
                            await useImInfoStore().getImInfo({ forceRefresh: true })
                            const ok = await this.sendCustomMessage(to, customExts, ext, { retryOnNotLogin: false })
                            return resolve(ok)
                        } catch (_) {}
                    }
                    message.error(`发送失败请重试：[${e.type}]${e.message}`)
                    useImInfoStore().getImInfo({ forceRefresh: true })
                    resolve(false)
                }
            })
        },
    },
})
